import type { ChangeRequest } from '../model/schema';
import { payloadBag } from './entity-bag';
import { operationLabel } from './status-labels';
import type { Translate } from './translate';

/** Short human summary of what changed (payload keys), for the detail modal. */
export function summarizePayload(payload: unknown): string[] {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return [];
  return Object.keys(payload).slice(0, 12);
}

/* ── Image patches ─────────────────────────────────────────────────── */
/**
 * `payload.image_patches` entries (`{Key, Value: {link, op, slot}}`, either
 * casing) — restaurant icon/background swaps arrive this way.
 */
export interface ImagePatch {
  key: string;
  slot: string;
  op: string;
  link: string;
}

function patchString(bag: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const value: unknown = bag[key];
    if (typeof value === 'string' && value.trim() !== '') return value;
  }
  return '';
}

function patchValueBag(bag: Record<string, unknown>): Record<string, unknown> {
  for (const key of ['Value', 'value']) {
    const nested: unknown = bag[key];
    if (nested !== null && typeof nested === 'object' && !Array.isArray(nested)) {
      return nested as Record<string, unknown>;
    }
  }
  return bag;
}

export function imagePatchesOf(request: Pick<ChangeRequest, 'payload'>): ImagePatch[] {
  const payload = payloadBag(request);
  const raw: unknown = payload.image_patches;
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item): ImagePatch[] => {
    if (item === null || typeof item !== 'object' || Array.isArray(item)) return [];
    const bag = item as Record<string, unknown>;
    const source = patchValueBag(bag);
    return [
      {
        key: patchString(bag, 'Key', 'key', 'id'),
        slot: patchString(source, 'slot', 'Slot'),
        op: patchString(source, 'op', 'Op'),
        link: patchString(source, 'link', 'Link', 'url'),
      },
    ];
  });
}

/* ── Change description ────────────────────────────────────────────── */
const KNOWN_CHANGE_KEYS = new Set([
  'lifecycle_status',
  'restaurant_price_patches',
  'companions',
  'image_patches',
  'restaurant_category_ids',
]);

/**
 * Human lines describing WHAT changes — so a price patch no longer renders as
 * an opaque `restaurant_price_patches` key. Each line is one aspect of the
 * change (publish, prices, linked groups); the raw keys stay available via
 * `summarizePayload` for anything unrecognized.
 */
export function describeChange(request: ChangeRequest, t: Translate): string[] {
  const payload = payloadBag(request);
  const lines: string[] = [];

  pushPublishLine(payload, lines, t);
  pushCountLine(
    payload,
    'restaurant_price_patches',
    'change.pricePatches',
    'Price update in {{count}} branch(es)',
    lines,
    t,
  );
  pushCountLine(
    payload,
    'companions',
    'change.companions',
    '{{count}} linked modifier group(s)',
    lines,
    t,
  );
  pushImagePatchLine(request, lines, t);
  pushCategoriesLine(payload, lines, t);
  pushOtherFieldsLine(payload, lines, t);

  if (lines.length === 0) {
    lines.push(operationLabel(request.operation, t));
  }
  return lines;
}

function pushPublishLine(
  payload: Record<string, unknown>,
  lines: string[],
  t: Translate,
): void {
  const lifecycle =
    typeof payload.lifecycle_status === 'string' ? payload.lifecycle_status : '';
  const normalized = lifecycle.toLowerCase();
  if (normalized.includes('life') && !normalized.includes('ready')) {
    lines.push(t('change.publish', 'Publish to the storefront'));
  }
}

function pushCountLine(
  payload: Record<string, unknown>,
  key: string,
  labelKey: string,
  fallback: string,
  lines: string[],
  t: Translate,
): void {
  const value: unknown = payload[key];
  if (!Array.isArray(value) || value.length === 0) return;
  lines.push(t(labelKey, fallback).replace('{{count}}', String(value.length)));
}

function pushImagePatchLine(request: ChangeRequest, lines: string[], t: Translate): void {
  const patches = imagePatchesOf(request);
  if (patches.length === 0) return;
  lines.push(
    t('change.imagePatches', 'Image update in {{count}} slot(s): {{slots}}')
      .replace('{{count}}', String(patches.length))
      .replace(
        '{{slots}}',
        [...new Set(patches.map((patch) => patch.slot || '—'))].join(', '),
      ),
  );
}

/**
 * Branch rubric assignment (`restaurant_category_ids`) — a plain id array on
 * creates, the PATCH-shaped `{ids: [...]}` wrapper on edits (absent = the
 * rubrics stay untouched). An empty set means every rubric was removed.
 */
function pushCategoriesLine(
  payload: Record<string, unknown>,
  lines: string[],
  t: Translate,
): void {
  const value: unknown = payload.restaurant_category_ids;
  if (value === undefined) return;
  const ids = Array.isArray(value)
    ? value
    : (value as Record<string, unknown> | null)?.ids;
  if (!Array.isArray(ids)) return;
  if (ids.length === 0) {
    lines.push(t('change.categoriesCleared', 'All categories removed'));
    return;
  }
  lines.push(
    t('change.categories', 'Categories: {{count}} selected').replace(
      '{{count}}',
      String(ids.length),
    ),
  );
}

function pushOtherFieldsLine(
  payload: Record<string, unknown>,
  lines: string[],
  t: Translate,
): void {
  const otherKeys = Object.keys(payload).filter((key) => !KNOWN_CHANGE_KEYS.has(key));
  if (otherKeys.length === 0) return;
  lines.push(
    t('change.otherFields', 'Other changed fields: {{keys}}').replace(
      '{{keys}}',
      otherKeys.join(', '),
    ),
  );
}
